import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { useLang } from "@/i18n/LanguageContext";
import { CallaLilyFlourish, RoseFlourish } from "@/components/wedding/Flourish";
import { Reveal } from "@/components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOME = { lng: 24.7536, lat: 59.437, city: "Tallinn", country: "Estonia", home: true };
const DEFAULT_CENTER = [45, 15];
const DEFAULT_ZOOM = 3;

// Imperative helper that lets us drive `flyTo` from outside the map.
const FlyController = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], target.zoom, { duration: 1.4 });
  }, [target, map]);
  return null;
};

export const GuestMap = () => {
  const { t } = useLang();
  const mapRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [ready, setReady] = useState(false);
  const [focused, setFocused] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/rsvp/locations`)
      .then((res) => {
        if (!alive) return;
        setLocations(Array.isArray(res.data) ? res.data : []);
        setReady(true);
      })
      .catch(() => alive && setReady(true));
    return () => { alive = false; };
  }, []);

  const markers = useMemo(() => {
    const isHome = (m) => Math.abs(m.lat - HOME.lat) < 0.15 && Math.abs(m.lng - HOME.lng) < 0.25;
    const guestMarkers = locations.map((m) => ({ ...m, home: isHome(m) }));
    return [HOME, ...guestMarkers.filter((m) => !m.home)];
  }, [locations]);

  const stats = useMemo(() => {
    const cities = new Set(locations.map((l) => `${l.city}|${l.country}`).filter((k) => k !== "|"));
    const countries = new Set(locations.map((l) => l.country).filter(Boolean));
    const guests = locations.reduce((s, l) => s + (l.count || 1), 0);
    return { cities: cities.size, countries: countries.size, guests };
  }, [locations]);

  const focusMarker = (m) => {
    setFocused(m);
    // Zoom 13 = street-level detail. Only the city + its streets remain visible.
    setFlyTarget({ lat: m.lat, lng: m.lng, zoom: 13 });
  };

  const resetView = () => {
    setFocused(null);
    setFlyTarget({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1], zoom: DEFAULT_ZOOM });
  };

  const heading = t("guestmap.heading");
  const isZoomed = !!focused;

  return (
    <section
      id="guestmap"
      data-testid="guestmap-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0A] text-[#FAFAFA] overflow-hidden"
    >
      <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(232,90,56,0.25) 0%, transparent 65%)" }}
           aria-hidden />
      <div className="absolute -bottom-24 -right-24 w-[560px] h-[560px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(255,180,120,0.15) 0%, transparent 65%)" }}
           aria-hidden />
      <CallaLilyFlourish
        className="absolute -top-6 -right-6 w-[100px] sm:w-[140px] md:w-[180px]"
        opacity={0.55}
        variant="dark"
      />
      <RoseFlourish
        className="absolute -bottom-8 -left-6 w-[100px] sm:w-[140px] md:w-[180px] rotate-6"
        opacity={0.7}
        variant="dark"
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <Reveal className="md:col-span-6">
            <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              {heading[0]}<em className="italic">{heading[1]}</em>{heading[2]}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6 md:pt-6">
            <p className="font-serif italic text-lg sm:text-xl text-white/70 max-w-md">
              {t("guestmap.sub")}
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <StatBlock value={stats.guests} label={t("guestmap.guests")} />
              <StatBlock value={stats.cities} label={t("guestmap.cities")} />
              <StatBlock value={stats.countries} label={t("guestmap.countries")} />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            className="relative border border-white/10 bg-[#050505] overflow-hidden"
            data-testid="guestmap-canvas"
            style={{ aspectRatio: "16 / 9" }}
          >
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              minZoom={2}
              maxZoom={18}
              scrollWheelZoom={true}
              worldCopyJump
              style={{ height: "100%", width: "100%", background: "#050505" }}
              ref={mapRef}
            >
              <TileLayer
                attribution='Tiles &copy; <a href="https://www.esri.com">Esri</a> &mdash; Esri, DeLorme, NAVTEQ'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              <FlyController target={flyTarget} />

              {ready && markers.map((m, i) => {
                const isFocused = focused && focused.lat === m.lat && focused.lng === m.lng;
                return (
                  <CircleMarker
                    key={`${m.lat}-${m.lng}-${i}`}
                    center={[m.lat, m.lng]}
                    radius={m.home ? 9 : 7}
                    pathOptions={{
                      color: m.home ? "#FFD79A" : "#E85A38",
                      fillColor: m.home ? "#FFD79A" : "#E85A38",
                      fillOpacity: isFocused ? 1 : 0.85,
                      weight: isFocused ? 3 : 2,
                    }}
                    eventHandlers={{ click: () => focusMarker(m) }}
                  >
                    <Popup closeButton={false} className="guestmap-popup">
                      <div className="font-overline text-[10px]" style={{ color: "#E85A38", letterSpacing: "0.2em" }}>
                        {m.home ? t("guestmap.home") : t("guestmap.travelling")}
                      </div>
                      <div className="font-title" style={{ fontSize: "18px", marginTop: 4, letterSpacing: "-0.01em" }}>
                        {m.city}{m.country ? `, ${m.country}` : ""}
                      </div>
                      {!m.home && m.count > 1 && (
                        <div className="font-serif italic" style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
                          {m.count} {t("guestmap.guestsLabel")}
                        </div>
                      )}
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>

            {/* Reset button */}
            <AnimatePresence>
              {isZoomed && (
                <motion.button
                  key="reset"
                  type="button"
                  data-testid="guestmap-reset"
                  onClick={resetView}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 z-[500] inline-flex items-center gap-2 border border-white/25 bg-black/80 backdrop-blur px-4 py-2.5 text-white font-overline text-[10px] hover:border-[color:var(--sunset-300)] hover:text-[color:var(--sunset-300)] transition-colors"
                  aria-label="Reset map view"
                >
                  <RotateCcw size={12} />
                  {t("guestmap.reset")}
                </motion.button>
              )}
            </AnimatePresence>

            {!locations.length && ready && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[400]">
                <div className="text-center px-6 bg-black/60 backdrop-blur py-6 border border-white/10">
                  <div className="font-overline text-[10px] text-white/50">{t("guestmap.emptyEyebrow")}</div>
                  <p className="mt-2 font-serif italic text-lg sm:text-xl text-white/85 max-w-md">
                    {t("guestmap.empty")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Reveal>

        <p className="mt-8 font-serif italic text-sm text-white/45 text-center">{t("guestmap.footer")}</p>
      </div>
    </section>
  );
};

const StatBlock = ({ value, label }) => (
  <div className="border-t border-white/15 pt-3">
    <div className="font-display text-4xl sm:text-5xl tracking-tighter">{value}</div>
    <div className="font-overline text-[10px] text-white/55 mt-1">{label}</div>
  </div>
);
